import { useEffect, useState } from "react";
import ApiConfig from "../api/config";
import { UserService } from "../services/UserService";
import { User } from "../storage/store";

// The MQTT client is loaded only on the pages that listen (it's ~340 KB).
// mqtt 5 is written in syntax webpack 4 can't read (?., class fields), so
// package.json's browserslist includes Safari 13, which makes Babel rewrite it.
const loadMqtt = () => import("mqtt/dist/mqtt.esm").then((module) => module.default ?? module);

// An energy meter's live readings, as the app's device screen listens to them
// (app/screens/CommonSwichDetails.js): a short-lived, listen-only login from
// the API, then the meter's live topic over MQTT. live holds the latest of
// every field, the energy totals included, as in the app; isLive is
// true while data comes in. The connection closes when the page is left or the
// browser tab is hidden, and opens again when it's shown.
export default function useMeterLive(device) {
  const applianceId = device?.applianceId;
  const [live, setLive] = useState(null);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    setLive(null);
    setIsLive(false);
    if (!applianceId) return undefined;
    let client = null;
    let stopped = false;
    let connecting = false;

    const close = () => {
      if (client) {
        client.end(true);
        client = null;
      }
      if (!stopped) setIsLive(false);
    };

    const connect = async () => {
      if (connecting || client || stopped || document.visibilityState === "hidden") return;
      connecting = true;
      try {
        const answer = await UserService.mqttCredentials(applianceId, User.getPropertyId());
        const { username, password, allowed_topics: topics } = answer?.mqtt_credentials ?? {};
        if (stopped || !username || !password || !Array.isArray(topics) || !topics.length) return;
        const mqtt = await loadMqtt();
        if (stopped) return;
        client = mqtt.connect(ApiConfig.mqttUrl, {
          clientId: `hashh_dashboard_meter_${applianceId}_${Math.random().toString(16).slice(2, 8)}`,
          username,
          password,
          clean: true,
          reconnectPeriod: 5000,
          connectTimeout: 10000,
        });
        client.on("connect", () => client?.subscribe(topics));
        client.on("message", (topic, message) => {
          try {
            const fields = JSON.parse(message.toString());
            if (stopped) return;
            setLive((current) => ({ ...current, ...fields }));
            setIsLive(true);
          } catch (error) {
            // not a reading: ignore it
          }
        });
        client.on("close", () => !stopped && setIsLive(false));
      } catch (error) {
        // no live feed: the saved readings stay on the page
      } finally {
        connecting = false;
      }
    };

    const onVisibility = () => (document.visibilityState === "hidden" ? close() : connect());
    connect();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisibility);
      close();
    };
  }, [applianceId]);

  return { live, isLive };
}
